import React, { useState, useEffect } from 'react';
import { getSuffererByID } from '../../../../controllers/SuffererController';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import avatarMale from '../../../../assets/images/avatars/avatar_male.png';
import avatarFemale from '../../../../assets/images/avatars/avatar_female.png';
import ButtonPrimary from '../../../../components/button/ButtonPrimary';
import ButtonSecondary from '../../../../components/button/ButtonSecondary';
import Skeleton from '../../../../components/Skeleton';

const DetailModal = ({ t }) => {
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [sufferer, setSufferer] = useState([]);
    const navigate = useNavigate();
    
    useEffect(() => {
        const fetchSufferer = async () => {
            try {
                const result = await getSuffererByID(id);
                if (!result) {
                    throw new Error(t('sufferer.fetchData.errorMessage'));
                };
                
                setSufferer(result);
            } catch (error) {
                toast.error(error.message);
                navigate('/sufferer');
            } finally {
                setLoading(false);
            };
        };
        
        fetchSufferer();
    }, [id, t, navigate]);
    
    if (loading) {
        return (
            <div className='space-y-5'>
                <Skeleton className='flex h-36 md:h-64 w-full rounded-lg' />
                <Skeleton className='flex h-36 md:h-64 w-full rounded-lg' />
            </div>
        ); 
    };
    
    const handleEdit = () => {
        navigate(`/sufferer/${id}/edit`, { state: { fromDetail: true } });
    };
    
    const handleBack = () => {
        navigate('/sufferer');
    };
    
    return (
        <div className='space-y-5'>
            {/* Avatar */}
            <div className='flex items-center justify-center'>
                <div
                    className={`
                        h-36 w-36 md:h-64 md:w-64 p-2 rounded-lg bg-gradient-to-r from-primary to-secondary
                        hover:outline-none hover:via-primary hover:to-secondary
                        focus:outline-none focus:to-primary
                    `}
                >
                    <div className='h-full w-full rounded bg-white shadow-[inset_0_4px_4px_rgba(0,0,0,0.2)] overflow-hidden'>
                        <img
                            className='h-full w-full mt-2'
                            src={sufferer.gender === 'Perempuan' ? avatarFemale : avatarMale}
                            alt=''
                        />
                    </div>
                </div>
            </div>
            
            {/* Sufferer Data, Emergency Contact, & Screening Log */}
            <div className='flex flex-col p-5 rounded-lg bg-white space-y-5'>
                {/* Sufferer Data */}
                <div className='space-y-5'>
                    <span className='text-base md:text-lg font-medium'>{t('sufferer.title')}:</span>
                    <div className='flex flex-grow items-center'>
                        <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.name.title')}:</span>
                        <input
                            className={`
                                w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                                hover:outline-none hover:border-grayX11
                                focus:outline-none focus:border-blackOlive
                            `}
                            type='text'
                            disabled
                            value={sufferer.name}
                        />
                    </div>
                    <div className='flex flex-grow items-center'>
                        <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.gender.title')}:</span>
                        <input
                            className={`
                                w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                                hover:outline-none hover:border-grayX11
                                focus:outline-none focus:border-blackOlive
                            `}
                            type='text'
                            disabled
                            value={t(`common.gender.${sufferer.gender.toLowerCase()}`)}
                        />
                    </div>
                    <div className='flex flex-grow items-center'>
                        <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.birthDate.title')}:</span>
                        <input
                            className={`
                                w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                                hover:outline-none hover:border-grayX11
                                focus:outline-none focus:border-blackOlive
                            `}
                            type='text'
                            disabled
                            value={sufferer.birth_date}
                        />
                    </div>
                    <div className='flex flex-grow items-center'>
                        <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.nik.title')}:</span>
                        <input
                            className={`
                                w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                                hover:outline-none hover:border-grayX11
                                focus:outline-none focus:border-blackOlive
                            `}
                            type='text'
                            disabled
                            value={sufferer.nik}
                        />
                    </div>
                    <div className='flex flex-grow items-center'>
                        <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.email.title')}:</span>
                        <input
                            className={`
                                w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                                hover:outline-none hover:border-grayX11
                                focus:outline-none focus:border-blackOlive
                            `}
                            type='text'
                            disabled
                            value={sufferer.email}
                        />
                    </div>
                    <div className='flex flex-grow items-center'>
                        <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.phoneNumber.title')}:</span>
                        <input
                            className={`
                                w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                                hover:outline-none hover:border-grayX11
                                focus:outline-none focus:border-blackOlive
                            `}
                            type='text'
                            disabled
                            value={'+62' + sufferer.phone_number}
                        />
                    </div>
                    <div className='flex flex-grow items-center'>
                        <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.address.title')}:</span>
                        <textarea
                            className={`
                                w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                                hover:outline-none hover:border-grayX11
                                focus:outline-none focus:border-blackOlive
                            `}
                            type='text'
                            disabled
                            value={sufferer.address}
                        />
                    </div>
                </div>
                
                {/* Emergency */}
                <div className='space-y-5'>
                    <span className='text-base md:text-lg font-medium'>{t('common.emergency.title')}:</span>
                    <div className='flex flex-grow items-center'>
                        <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.name.title')}:</span>
                        <input
                            className={`
                                w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                                hover:outline-none hover:border-grayX11
                                focus:outline-none focus:border-blackOlive
                            `}
                            type='text'
                            disabled
                            value={sufferer.emergency.name}
                        />
                    </div>
                    <div className='flex flex-grow items-center'>
                        <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.emergency.relation.title')}:</span>
                        <input
                            className={`
                                w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                                hover:outline-none hover:border-grayX11
                                focus:outline-none focus:border-blackOlive
                            `}
                            type='text'
                            disabled
                            value={sufferer.emergency.relation}
                        />
                    </div>
                    <div className='flex flex-grow items-center'>
                        <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.phoneNumber.title')}:</span>
                        <input
                            className={`
                                w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                                hover:outline-none hover:border-grayX11
                                focus:outline-none focus:border-blackOlive
                            `}
                            type='text'
                            disabled
                            value={'+62' + sufferer.emergency.phone_number}
                        />
                    </div>
                </div>
                <div className='flex justify-center sm:justify-end space-x-5'>
                    <ButtonSecondary className='h-full w-24 md:w-32 md:text-sm' onClick={handleBack}>{t('common.action.back')}</ButtonSecondary>
                    <ButtonPrimary className='h-full w-24 md:w-32 md:text-sm' onClick={handleEdit}>{t('common.action.edit')}</ButtonPrimary>
                </div>
            </div>
        </div>
    );
};

export default DetailModal;
